import React, { useCallback, useMemo } from 'react';
import moment from 'moment';

import Inputs from 'components/Inputs';
import Typography from 'kingpin/atoms/Typography';
import { LabelDiv } from 'screens/Boards/PerformanceBoardForm';
import Row from 'components/Common/Row';
import Toggle from 'components/Inputs/Toggle';
import Tooltip from 'components/Tooltip';
import Dropdown from 'components/Inputs/Dropdown';
import useSlideoutDateScope from 'components/PerformanceBoards/hooks/useSlideoutDateScope';
import useIntervalConfig from './useIntervalConfig';

const IntervalPickerLabel = () => {
  const { interval, intervalLength } = useIntervalConfig();

  return (
    <LabelDiv>
      <Row style={{ gap: '0.25em' }}>
        <Typography.Body type="Label">How many complete </Typography.Body>
        <Tooltip
          content={`${intervalLength} ${interval}`}
          tag={'span'}
          position="top"
          isFlexAnchor
        >
          <Typography.Body
            type="Label"
            isUnderlined
            color={'var(--color-brand-primary)'}
          >
            intervals
          </Typography.Body>
        </Tooltip>
        <Typography.Body type={'Label'}>
          would you like to display
        </Typography.Body>
      </Row>
    </LabelDiv>
  );
};

const IntervalPicker = ({
  widgetDateRange,
  setWidgetDateRange,
}: {
  widgetDateRange: PerformanceBoardTypes.SlideOut.SlideOutDateRange;
  setWidgetDateRange: React.Dispatch<
    React.SetStateAction<PerformanceBoardTypes.SlideOut.SlideOutDateRange>
  >;
}) => {
  const dateRange = useSlideoutDateScope(widgetDateRange);

  const getDateRangeText = (dateRange: DateRangeInput | undefined) => {
    if (!dateRange) {
      return '...';
    }
    const startText = moment(dateRange.startDate).format('MM/DD/YY');
    const endText = moment(dateRange.endDate).format('MM/DD/YY');

    return `${startText} - ${endText}`;
  };

  const onNumberOfIntervalsChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newNumber = event.target.value;
      if (!newNumber || widgetDateRange.mode === 'Current Interval') {
        return;
      }

      setWidgetDateRange((current) => {
        if (current.mode === 'Current Interval') {
          return current;
        }

        return {
          ...current,
          n: Number(newNumber) < 1 ? 1 : Number(newNumber),
        };
      });
    },
    [setWidgetDateRange, widgetDateRange],
  );

  const onIncludeDateChanged = useCallback(
    (newToDate: boolean) => {
      setWidgetDateRange((current) => ({
        ...current,
        isToDate: newToDate,
      }));
    },
    [setWidgetDateRange],
  );

  const modeOptions = useMemo((): DropdownOption[] => {
    return [
      {
        label: 'Current Interval',
        isSelected: widgetDateRange.mode === 'Current Interval',
        onSelected: () => {
          setWidgetDateRange({
            mode: 'Current Interval',
            isToDate: true,
          });
        },
      },
      {
        label: 'Last',
        isSelected: widgetDateRange.mode === 'Last',
        onSelected: () => {
          setWidgetDateRange({
            mode: 'Last',
            isToDate: false,
            n: 1,
          });
        },
      },
    ];
  }, [setWidgetDateRange, widgetDateRange.mode]);

  return (
    <>
      <IntervalPickerLabel />
      <Row style={{ marginBottom: 8 }}>
        <Dropdown options={modeOptions} noRightBorder />
        {widgetDateRange.mode === 'Last' && (
          <Inputs.TextInput
            data-testid="date-value-input"
            type="number"
            state={widgetDateRange.n === undefined ? 'Error' : undefined}
            value={widgetDateRange.n ? widgetDateRange.n.toString(10) : ''}
            onChange={onNumberOfIntervalsChanged}
            style={{
              marginLeft: '-1px',
              borderBottomLeftRadius: 0,
              borderTopLeftRadius: 0,
            }}
          />
        )}
      </Row>
      <Row centerAlign style={{ marginBottom: 8 }}>
        <div style={{ marginRight: 8 }}>
          <Toggle
            value={widgetDateRange.isToDate}
            onChange={(newValue) => onIncludeDateChanged(newValue)}
          />
        </div>
        <Typography.Body type="Body 12">
          Include interval to date
        </Typography.Body>
      </Row>
      <Typography.Body type="Body 12">
        {`Selected rolling period as of today: ${getDateRangeText(dateRange)}`}
      </Typography.Body>
    </>
  );
};

export default IntervalPicker;
